import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { getAbsolutePlayerTimePure } from '@goldcast/player-config/src/utils/timeUtils';
import { getAllCompositeSegments, getClipFPS, isClipFromUploads } from '@goldcast/player-config/src/utils/clipUtils';
import { isLayoutAudiogram } from '@goldcast/player-config/src/utils/layout';
import { CompositeSegment } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import { SegmentType } from '@goldcast/player-config/src/types/Segment/Type';
import AudiogramPreview from '../AudiogramPreview';
import { DEFAULT_TILE_CURSOR, PROCESSING_CURSOR, UNAVAILABLE_CURSOR } from '../../MagicLayout/constants';
import { PreviewTemplate } from '../types';
import CanvasPreview from '../../CanvasPlayer/CanvasPreview/CanvasPreview';
import TemplateMenu from './TemplateMenu';
import IntroOutroPlayer from '../../ClipPlayer/IntroOutroPlayer';
import { useActiveSpeakerMatchCondition } from '../../ClipPlayer/ClipCustomizer/useActiveSpeakerMatchCondition';
import { useNewTemplatePreview } from './useNewTemplatePreview';
import SceneThumbnail from '../../ClipPlayer/ClipNewTimeline/TimelineSegments/TimelineScenes/SceneThumbnail';
import { remotionPlayerStateStore } from '../../ClipPlayer/ClipNewTimeline/store/remotionPlayerState';
import { getPreviousSegmentAbsoluteTime, getSegmentAtCurrentFrame } from '../../ClipPlayer/ClipNewTimeline/timeUtils';
import { zoomLevelStore } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/store/zoomLevel';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { classnames } from '@/libs/utils';
import { useSpeakerSegmentContext } from '@/context/SpeakerSegmentContext/SpeakerSegmentContext';
import { applyTemplate, updateClipWithId } from '@/stores/clip';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import { isCustomUpload } from '@/libs/clipContentUtil';
import ConfirmationDialog from '@/components/organisms/ConfirmationDialog';
import useDialog from '@/components/organisms/useDialog';
import Tooltip, { TooltipPosition } from '@/components/atoms/Button/Tooltip';
import Marquee from '@/components/atoms/Marquee/Marquee';
import useAnalytics from '@/hooks/useAnalytics';
import { core } from '@/stores/core';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { ResponsiveContainer } from '@/components/atoms/ResponsiveContainer';
import { useSceneThumbnail } from '@/Pages/TranscriptPage/useSceneThumbnail';
import Icon from '@/components/atoms/Icon';

export default function TemplatePreview({
  template,
  classNames = '',
  canSelect = true,
  predefinedTooltipPosition = 'left',
  isSpeakerIdentificationMandatory = false
}: {
  template: PreviewTemplate;
  classNames?: string;
  canSelect?: boolean;
  predefinedTooltipPosition?: TooltipPosition;
  isSpeakerIdentificationMandatory?: boolean;
}) {
  const { clipId, clipData, sharedAPIStore } = useClipsContext();
  const { trackTemplateSelected, trackTemplateApplied } = useAnalytics();
  const coreStore = useSyncExternalStore(core.subscribe, core.getSnapshot);
  const speakerSegmentContextData = useSpeakerSegmentContext();
  const [isMarqueeActive, setIsMarqueeActive] = useState(false);
  const remotionPlayerState = useSyncExternalStore(
    remotionPlayerStateStore.subscribe,
    remotionPlayerStateStore.getSnapshot
  );

  const titleContainerRef = useRef<HTMLDivElement>(null);
  const disabledBadge = useRef<HTMLDivElement>(null);

  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition>('left');

  useEffect(() => {
    if (!!titleContainerRef.current && !predefinedTooltipPosition) {
      setTooltipPosition(titleContainerRef.current.getBoundingClientRect().left > 50 ? 'left' : 'right');
    }
  }, [titleContainerRef, predefinedTooltipPosition]);

  const currentSpeaker = useMemo(() => {
    return speakerSegmentContextData?.segmentSpeaker[speakerSegmentContextData?.startSegment];
  }, [speakerSegmentContextData]);

  const secondaryPlayers = useMemo(() => {
    return Object.values(sharedAPIStore.speakerMap).map(speaker => ({ current: speaker.videoRef }));
  }, [sharedAPIStore.speakerMap]);

  const isActiveSpeakerPlayable = useMemo(() => {
    if (template.isUnavailable && template.reason === 'MORE_THAN_ONE_SPEAKER_DETECTED') {
      return true;
    }

    return template.layout === 'SPEAKER' && isSpeakerIdentificationMandatory;
  }, [template.isUnavailable, template.reason, template.layout, isSpeakerIdentificationMandatory]);

  const isNewABTypeLayoutProcessing = useMemo(
    () => coreStore.content?.media_source_type === 'UPLOAD' && template.isProcessing,
    [template.isProcessing, coreStore]
  );

  const isDisabled = useMemo(() => {
    if (isNewABTypeLayoutProcessing) {
      return false;
    }

    if (template.isProcessing) {
      return true;
    }

    return template.isUnavailable && template.reason !== 'MORE_THAN_ONE_SPEAKER_DETECTED';
  }, [isNewABTypeLayoutProcessing, template.isProcessing, template.isUnavailable, template.reason]);

  const mainPlayerRef = useMemo(() => {
    return { current: sharedAPIStore.mainPlayerRef };
  }, [sharedAPIStore.mainPlayerRef]);

  const { shouldBehaveLikeActiveSpeaker } = useActiveSpeakerMatchCondition();

  function openSpeakersIdentificationDialog() {
    EventBus.dispatch(CustomEvents.OpenSpeakersIdentification, {
      postIdentifyCallback: dispatchReloadTemplates,
      layout: shouldBehaveLikeActiveSpeaker ? 'SPEAKER' : template.layout,
      isInlineIdentification: true
    });
  }

  function dispatchReloadTemplates() {
    updateClipWithId(clipId, {
      layout_status: {
        ...clipData.layout_status,
        SPEAKER: {
          status: 'DONE',
          reason: '',
          accurate: clipData.layout_status.SPEAKER.accurate
        }
      }
    });

    EventBus.dispatch(CustomEvents.ReloadTemplates, { template });
  }

  const isUpload = isCustomUpload();
  const {
    isOpen: isActiveSpeakerIdentificationRequiredDialogOpen,
    openDialog: openActiveSpeakerIdentificationRequiredDialog,
    closeDialog: closeActiveSpeakerIdentificationRequiredDialog
  } = useDialog();

  const {
    isOpen: isAudiogramConfirmationOpen,
    openDialog: openAudiogramConfirmationDialog,
    closeDialog: closeAudiogramConfirmationDialog
  } = useDialog();

  const {
    isOpen: isScenesConfirmationOpen,
    openDialog: openScenesConfirmationDialog,
    closeDialog: closeScenesConfirmationDialog
  } = useDialog();

  const shouldHighlightTemplatePreview = useMemo(() => {
    const isClipEdited = clipData.asset_metadata.is_edited;
    if (isClipEdited) {
      return false;
    }

    const template_id = clipData.asset_metadata.template_id;
    if (!template_id && template.default === coreStore.content?.media_type) {
      return true;
    }

    return template_id === template.id;
  }, [clipData, template.id]);

  function onTemplatePreviewClick() {
    if (!(canSelect && !isDisabled)) {
      return;
    }

    trackTemplateSelected({
      templateId: template.id,
      templateName: template.title,
      templateType: template.isDefault ? 'Preset' : 'My Templates',
      layout: template.layout
    });

    trackTemplateApplied({
      templateId: template.id,
      templateName: template.title,
      templateSource: template.isDefault ? 'system' : 'user',
      clipId,
      layout: template.layout
    });

    openConfirmationDialogsOrApplyTemplate();
  }

  function openConfirmationDialogsOrApplyTemplate() {
    const scenes = clipData.asset_metadata.scenes;

    if (template.layout === 'SPEAKER' && isCustomUpload() && isActiveSpeakerPlayable) {
      openActiveSpeakerIdentificationRequiredDialog();
    } else if (scenes && isLayoutAudiogram(template.layout)) {
      openAudiogramConfirmationDialog();
    } else if (scenes && scenes.length > 1) {
      openScenesConfirmationDialog();
    } else {
      applyTemplate(clipId, template);
    }
  }

  const applyAudiogramTemplate = useCallback(() => {
    applyTemplate(clipId, template);
    closeAudiogramConfirmationDialog();
  }, [clipId, template, closeAudiogramConfirmationDialog]);

  const applyTemplateWithScenesConfirmation = useCallback(() => {
    applyTemplate(clipId, template);
    closeScenesConfirmationDialog();
  }, [clipId, template, closeScenesConfirmationDialog]);

  const { shouldUseNewTemplatePreview, compositionHeight, compositionWidth, backgroundStyle, videos, finalConfig } =
    useNewTemplatePreview({
      template,
      clip: clipData
    });

  const shouldUseSceneThumbnailForPreview = useMemo(() => {
    return !template.intro && !canSelect && isClipFromUploads(clipData);
  }, [canSelect, template.intro]);

  const currentTimeForUploads = useMemo(() => {
    if (!clipData.asset_metadata.player_config || !shouldUseSceneThumbnailForPreview) {
      return -1;
    }

    const currentTime = remotionPlayerState.currentTime;

    const currentSegment = getSegmentAtCurrentFrame(currentTime, clipData);

    if (!currentSegment || currentSegment.type !== SegmentType.COMPOSITE) {
      return -1;
    }

    const trimOffset = currentSegment.currentBounds.fromFrame / getClipFPS(clipData);

    // Calculate the current frame position, offset by the composite segment's start frame
    const currentSegmentTime =
      getAbsolutePlayerTimePure({
        time: currentTime,
        clipData,
        minSegmentWidth: -Infinity,
        zoomLevel: zoomLevelStore.getSnapshot().zoomLevel,
        isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
      }) -
      getPreviousSegmentAbsoluteTime(currentSegment, clipData, -Infinity) +
      trimOffset;

    return currentSegmentTime;
  }, [clipData, remotionPlayerState.currentTime, shouldUseSceneThumbnailForPreview]);

  const compositeSegment = useMemo(() => {
    return getAllCompositeSegments(clipData)[0];
  }, [clipData]);

  const { playerConfig, croppedImageUrl } = useSceneThumbnail({
    clip: clipData,
    startTimeInSeconds: currentTimeForUploads
  });

  const showSceneThumbnailForPreview = useMemo(() => {
    return shouldUseSceneThumbnailForPreview && playerConfig && croppedImageUrl && currentTimeForUploads >= 0;
  }, [shouldUseSceneThumbnailForPreview, playerConfig, croppedImageUrl, currentTimeForUploads]);

  return (
    <div onMouseEnter={() => setIsMarqueeActive(true)} onMouseLeave={() => setIsMarqueeActive(false)}>
      <div
        className={classnames(
          'relative flex aspect-[4/3] w-full shrink-0 items-center justify-center rounded-lg border-2 border-transparent bg-slate-100 p-5 text-left',
          canSelect ? 'cursor-pointer' : '',
          classNames,
          shouldHighlightTemplatePreview ? 'ring-2 ring-theme-border' : ''
        )}
        data-testid="template-preview-container"
        onClick={onTemplatePreviewClick}
      >
        {showSceneThumbnailForPreview ? (
          <div className="flex size-full justify-center">
            <SceneThumbnail
              playerConfig={playerConfig}
              videoSize={clipData.asset_metadata.size}
              thumbnailFrameToDisplay={currentTimeForUploads}
              compositeSegment={compositeSegment as CompositeSegment}
              compositionHeight={compositionHeight}
              compositionWidth={compositionWidth}
              mediaContent={clipData.content}
              croppedImageUrl={croppedImageUrl}
              parentWidth={'100%'}
              sceneThumbnailResponsiveContainerChildClassnames="m-auto"
            />
          </div>
        ) : shouldUseNewTemplatePreview ? (
          <ResponsiveContainer
            id="template-preview-container"
            compositionHeight={compositionHeight}
            compositionWidth={compositionWidth}
            parentHeight={'100%'}
            parentWidth={'100%'}
            childClassnames="m-auto"
          >
            <div
              style={{
                position: 'relative',
                height: '100%',
                width: '100%',
                ...backgroundStyle
              }}
            >
              {videos.map((video, videoIndex) => {
                const newStyle = finalConfig[videoIndex].style;
                const newContainerStyle = finalConfig[videoIndex].containerStyle;

                return (
                  <div
                    key={`video-${videoIndex}`}
                    style={{ ...newContainerStyle, borderRadius: '1px' } as CSSProperties}
                  >
                    <img src={video || ''} alt="video" style={{ ...newStyle } as CSSProperties} />
                  </div>
                );
              })}
            </div>
          </ResponsiveContainer>
        ) : (
          <div
            className={classnames('relative flex items-center justify-center rounded-md', {
              'aspect-video w-full': template.size === 'LANDSCAPE',
              'aspect-square h-full': template.size === 'SQUARE',
              'aspect-[9/16] h-full': template.size === 'PORTRAIT'
            })}
          >
            {isDisabled || isActiveSpeakerPlayable ? (
              <div
                className={classnames('flex size-full items-center justify-center', {
                  [PROCESSING_CURSOR]: !isNewABTypeLayoutProcessing && template.isProcessing,
                  [UNAVAILABLE_CURSOR]: template.isUnavailable,
                  [DEFAULT_TILE_CURSOR]: isActiveSpeakerPlayable
                })}
              >
                <div className="flex size-full items-center justify-center rounded">
                  <Icon name="IconVideo" size={32} stroke={1.5} className="text-slate-500" />
                </div>
              </div>
            ) : template.intro ? (
              <ResponsiveContainer
                id="template-preview-container"
                compositionHeight={compositionHeight}
                compositionWidth={compositionWidth}
                parentHeight={'100%'}
                parentWidth={'100%'}
                childClassnames="m-auto"
              >
                <IntroOutroPlayer
                  videoProps={{
                    src: template.intro
                  }}
                  type="intro"
                  parentClassName="h-full"
                />
              </ResponsiveContainer>
            ) : template.layout === 'AUDIOGRAM' && currentSpeaker ? (
              <AudiogramPreview clipTemplate={template} currentSpeaker={currentSpeaker} />
            ) : template.layout === 'DEFAULT' && !!mainPlayerRef.current ? (
              <CanvasPreview template={template} mainPlayer={mainPlayerRef} secondaryPlayers={[]} />
            ) : ['SPEAKER', 'GRID'].includes(template.layout) &&
              !!mainPlayerRef.current &&
              (isUpload || !!secondaryPlayers.length) ? (
              <CanvasPreview
                template={{ ...clipData.asset_metadata, ...template }}
                mainPlayer={mainPlayerRef}
                secondaryPlayers={secondaryPlayers}
                currentSpeaker={currentSpeaker}
              />
            ) : (
              <></>
            )}
          </div>
        )}
        {!template.isDefault && canSelect && <TemplateMenu template={template} />}
      </div>
      {template.title && (
        <div className="flex items-center justify-between text-xs">
          <div
            className={classnames('top-1 mt-1.5 line-clamp-1 w-full text-xs', { 'text-slate-600': isDisabled })}
            ref={titleContainerRef}
          >
            <Marquee content={template.title} isActive={isMarqueeActive} offset={disabledBadge.current?.offsetWidth} />
          </div>
          {isDisabled && (
            <div
              className="group relative ml-0.5 flex cursor-help items-center space-x-0.5 rounded bg-slate-300 px-1 py-0.5 text-xs text-slate-600"
              ref={disabledBadge}
            >
              <Icon name="IconInfoCircle" size={16} />
              <span>{template.isUnavailable ? 'Unavailable' : 'Processing'}</span>
              {template.isUnavailable && (
                <Tooltip content={template.message || ''} position={predefinedTooltipPosition || tooltipPosition} />
              )}
            </div>
          )}
        </div>
      )}
      <ConfirmationDialog
        isOpen={isActiveSpeakerIdentificationRequiredDialogOpen}
        onClose={closeActiveSpeakerIdentificationRequiredDialog}
        onConfirm={openSpeakersIdentificationDialog}
        title="Identify speakers to use this template"
        content={'Match speakers to faces in the video to use the Active Speaker template.'}
        confirmLabel="Next"
      />
      <ConfirmationDialog
        isOpen={isAudiogramConfirmationOpen}
        onClose={closeAudiogramConfirmationDialog}
        onConfirm={applyAudiogramTemplate}
        title="Confirm Layout Change"
        content="This will delete all existing scenes in this segment and replace them with a single layout. Are you sure you want to continue?"
        confirmLabel="Continue"
        cancelLabel="Cancel"
      />
      <ConfirmationDialog
        isOpen={isScenesConfirmationOpen}
        onClose={closeScenesConfirmationDialog}
        onConfirm={applyTemplateWithScenesConfirmation}
        title="Confirm Template Application"
        content="This will update all existing scenes with the same layout and fit style. Are you sure you want to continue?"
        confirmLabel="Continue"
        cancelLabel="Cancel"
      />
    </div>
  );
}
